'use client'

import { useForm } from 'react-hook-form'

import {
  buildSeedAssertionWrites,
  toPlainAmount,
} from '~/app/accounting/lot-assertion-writes'
import { LotRow } from '~/app/accounting/lot-rows'
import { useLotAssertionWrite } from '~/app/accounting/useLotAssertionWrite'
import { DateInput, NumberInput } from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { LabelValue } from '~/components/component-library/LabelValue'

type SeedLotFormValues = {
  quantity: string
  unitPrice: string
  acquisitionTime: Date | null
}

/**
 * Seeds pre-custody inventory into the transfer that opened this lot: the
 * form captures what was held before Splits custody (amount, basis, and
 * since when), and the write pairs the seed with a designation of the lot's
 * source transfer, so the seeded holdings carry into this lot's inbound
 * non-taxably instead of opening at its arrival-day price.
 */
export const SeedLotDialog = ({ lot }: { lot: LotRow }) => {
  const { writeAssertionBundle, isProcessing } = useLotAssertionWrite()

  const { control, watch } = useForm<SeedLotFormValues>({
    mode: 'onChange',
    defaultValues: {
      // The whole transfer is the common case; a partial seed leaves the
      // rest of the inbound at its indexed price.
      quantity: toPlainAmount(lot.quantity),
      unitPrice: '',
      acquisitionTime: null,
    },
  })

  const values = watch()
  // Undefined until submittable, driving both the disabled state and the body.
  const writes = buildSeedAssertionWrites({
    lot,
    quantity: values.quantity ?? '',
    unitPrice: values.unitPrice ?? '',
    acquisitionTime: values.acquisitionTime,
  })

  const submit = async () => {
    if (!writes) return
    await writeAssertionBundle({
      assertions: writes,
      successMessage: 'Lot seeded',
      successDescription:
        'The original holdings will be applied to your totals soon',
      errorMessage: 'Failed to seed lot',
    })
  }

  return (
    <SplitsDialogContent
      id="seedLot"
      variant="cta"
      title={`Seed lot ${lot.lotNumber}`}
      isLoading={isProcessing}
      actionButton={{
        text: 'Seed lot',
        onClick: submit,
        isDisabled: writes === undefined,
      }}
      cancelButton={{ text: 'Cancel' }}
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-secondary">
          Enter what these holdings were worth when you originally acquired
          them, before they arrived here. Your cost basis and holding period
          carry over instead of restarting on arrival.
        </p>

        <div className="flex flex-col gap-2">
          <LabelValue
            size="small"
            label="Wallet"
            value={lot.smartAccountName}
          />
          <LabelValue
            size="small"
            label="Token"
            value={lot.symbol || lot.tokenAddress}
          />
        </div>

        <NumberInput
          control={control}
          inputName="quantity"
          label="Amount"
          description="How much of this transfer came from your original holdings"
          unit={lot.symbol || undefined}
          decimalScale={lot.decimals}
          placeholder="0"
        />
        <NumberInput
          control={control}
          inputName="unitPrice"
          label="Original price"
          description="USD per token when you originally acquired it"
          prefix="$"
          decimalScale={18}
          placeholder="0.00"
          autoFocus
        />
        <DateInput
          control={control}
          inputName="acquisitionTime"
          label="Original acquisition date"
          maxDate={new Date()}
          showTimeSelect
        />
      </div>
    </SplitsDialogContent>
  )
}
